import React, { useState, useRef, useEffect } from "react";
import Button from "@mui/material/Button";
import { toPng } from "html-to-image";
import moment from "moment";
import PaymentService from "../../services/payment";
import { useParams } from "react-router-dom";

function formatDate(rawDate) {
  if (!rawDate) return "N/A";
  const m = moment.utc(rawDate, ["YYYY-MM-DD", moment.ISO_8601], true);
  return m.isValid() ? m.format("DD/MM/YYYY") : String(rawDate);
}

const CertificateHtml = () => {
  const [certificateUrl, setCertificateUrl] = useState();
  const [paymentData, setPaymentData] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(null);

  const elementRef = useRef(null);
  const params = useParams(null);
  console.log(params.transaction_id);

  useEffect(() => {
    const transactionId = params.transaction_id;
    if (!transactionId) return;

    const fetchPayment = (attempt = 0) => {
      const maxAttempts = 5;
      const delayMs = attempt === 0 ? 0 : 800 * attempt;

      const doFetch = () =>
        PaymentService.fetchPaymentByTransId(transactionId)
          .then((res) => {
            const row = res?.data?.data?.[0];
            if (row) {
              setPaymentData(row);
              return;
            }
            if (attempt < maxAttempts - 1) {
              setTimeout(() => fetchPayment(attempt + 1), delayMs);
            } else {
              setPaymentData(null);
            }
          })
          .catch((err) => {
            console.error("Fetch payment error:", err);
            if (attempt < maxAttempts - 1) {
              setTimeout(() => fetchPayment(attempt + 1), delayMs);
            } else {
              setPaymentData(null);
            }
          });

      if (delayMs > 0) setTimeout(doFetch, delayMs);
      else doFetch();
    };

    fetchPayment();
  }, [params.transaction_id]);

  useEffect(() => {
    if (!paymentData || !paymentData.transaction_id) return;
    if (paymentData.certificate_url) {
      setCertificateUrl(paymentData.certificate_url);
      return;
    }
    setIsGenerating(true);
    setGenerationError(null);
    const logoImg = new Image();
    logoImg.crossOrigin = "anonymous";
    logoImg.onload = () => htmlToImageConvert();
    logoImg.onerror = () => htmlToImageConvert();
    logoImg.src = "/shaagird-logo.png";
  }, [paymentData]);

  const htmlToImageConvert = () => {
    if (!paymentData?.transaction_id) {
      setGenerationError("Payment data not ready. Please refresh the page.");
      setIsGenerating(false);
      return;
    }
    if (!elementRef.current) return;
    const images = elementRef.current.querySelectorAll("img");
    const imagePromises = Array.from(images).map((img) => {
      if (img.complete) {
        return Promise.resolve();
      } else {
        return new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });
      }
    });

    Promise.all(imagePromises)
      .then(() => new Promise((resolve) => setTimeout(resolve, 150)))
      .then(() => {
        // Capture as-rendered (same as admin – no width/height override)
        return toPng(elementRef.current, {
          cacheBust: false,
          backgroundColor: "#ffffff",
          useCORS: true,
          pixelRatio: 2,
        });
      })
      .then(async (dataUrl) => {
        setCertificateUrl(dataUrl);
        const txnId = paymentData?.transaction_id;
        if (!txnId) {
          setIsGenerating(false);
          setGenerationError("Payment reference missing. Please refresh.");
          return;
        }
        setIsGenerating(false);
        try {
          const res = await PaymentService.saveCertificate({
            transaction_id: txnId,
            certificate_data_url: dataUrl,
          });
          const url = res?.data?.certificate_url;
          if (url) setCertificateUrl(url);
          setGenerationError(null);
        } catch (err) {
          console.error("Certificate save error:", err);
          setGenerationError(
            err?.response?.data?.message ||
              "Certificate could not be saved. Please try again or contact support.",
          );
        }
      })
      .catch((err) => {
        console.log("Error generating certificate:", err);
        setGenerationError("Failed to generate certificate. Please try again.");
        setIsGenerating(false);

        // Fallback: same options as main path
        toPng(elementRef.current, {
          cacheBust: false,
          backgroundColor: "#ffffff",
          useCORS: true,
          pixelRatio: 2,
        })
          .then(async (dataUrl) => {
            setCertificateUrl(dataUrl);
            setIsGenerating(false);
            const txnId = paymentData?.transaction_id;
            if (!txnId) {
              setGenerationError("Payment reference missing. Please refresh.");
              return;
            }
            try {
              const res = await PaymentService.saveCertificate({
                transaction_id: txnId,
                certificate_data_url: dataUrl,
              });
              const url = res?.data?.certificate_url;
              if (url) setCertificateUrl(url);
              setGenerationError(null);
            } catch (err) {
              console.error("Fallback certificate save error:", err);
              setGenerationError(
                err?.response?.data?.message ||
                  "Certificate could not be saved. Please try again.",
              );
            }
          })
          .catch((fallbackErr) => {
            console.log("Fallback generation failed:", fallbackErr);
            setGenerationError(
              "Unable to generate certificate. Please contact support.",
            );
            setIsGenerating(false);
          });
      });
  };

  const onReceiptDownload = () => {
    const link = document.createElement("a");
    link.download = "donation-receipt.png";
    link.href = certificateUrl;
    link.click();
  };

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h5 className="mb-0">Certificate</h5>
        <div className="d-flex align-items-center gap-2">
          {isGenerating && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: "#299D8E",
                fontSize: "14px",
              }}
            >
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              Generating...
            </div>
          )}
          {generationError && (
            <div
              style={{
                color: "#dc3545",
                fontSize: "14px",
                marginRight: "10px",
              }}
            >
              {generationError}
            </div>
          )}
          {certificateUrl && (
            <Button
              sx={{
                backgroundColor: "#299D8E",
                "&:hover": {
                  backgroundColor: "#2BBBAD",
                },
              }}
              variant="contained"
              onClick={onReceiptDownload}
            >
              Download Certificate
            </Button>
          )}
        </div>
      </div>

      {/* Wrapper: same width as admin modal so alignment matches exactly */}
      <div className="receipt" style={{ maxWidth: 665, margin: "0 auto" }}>
        <div
          ref={elementRef}
          style={{
            position: "relative",
            boxSizing: "border-box",
            width: "100%",
            backgroundColor: "#fff",
            padding: 20,
            fontFamily: "Arial, Helvetica, sans-serif",
            color: "#000",
          }}
        >
          <img
            src="/img/receiptHeader.png"
            alt="Header bg"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              zIndex: 0,
            }}
          />

          <img
            src="/img/header1.png"
            alt="Header"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              zIndex: 0,
            }}
          />

          {/* Main Content – pixel match to admin */}
          <div
            style={{ position: "relative", zIndex: 1, margin: 0, padding: 0 }}
          >
            {/* Logo: top-left, no extra gap */}
            <img
              src="/img/logo.png"
              alt="Logo"
              style={{
                display: "block",
                width: "100px",
                height: "auto",
                margin: "0 0 20px 0",
                padding: 0,
                verticalAlign: "top",
              }}
            />

            {/* Foundation name + address: same font/size as admin, left of content */}
            <div
              style={{
                position: "absolute",
                top: 0,
                left: "120px",
                fontFamily: "Georgia, Times New Roman, serif",
                fontSize: "12px",
                lineHeight: 1.5,
                color: "#000",
                zIndex: 0,
                margin: 0,
                padding: 0,
              }}
            >
              <div
                style={{
                  fontSize: "22px",
                  fontWeight: "bold",
                  lineHeight: 1.3,
                  marginBottom: "6px",
                }}
              >
                SHAAGIRD FOUNDATION
              </div>
              <p style={{ margin: "0 0 4px 0", fontSize: "12px" }}>
                H No. 399 Prop No. 87G/F, Main 25 Ft Road, Shiv Vihar,
                <br />
                Karawal Nagar, North East Delhi 11009
                <br />
                contact@shaagird.org
                <br />
                <b>PAN:</b>ABJCS7197K <b style={{ marginLeft: 10 }}>CIN:</b>{" "}
                U80901DL2022NPL406795
              </p>
            </div>

            {/* Title: sans-serif, centered, with underline */}
            <div
              style={{
                textAlign: "center",
                color: "#000",
                marginTop: "88px",
                marginBottom: 0,
              }}
            >
              <h1
                style={{
                  fontFamily: "Arial, Helvetica, sans-serif",
                  fontSize: "30px",
                  fontWeight: 700,
                  margin: "0 0 10px 0",
                  paddingBottom: "8px",
                  letterSpacing: "0.02em",
                }}
              >
                DONATION RECEIPT
              </h1>
            </div>

            {/* Gratitude: serif italic, same as admin */}
            <div
              style={{
                position: "relative",
                textAlign: "center",
                marginTop: "18px",
                marginBottom: 0,
              }}
            >
              <img
                style={{ position: "absolute", width: 30, left: -5, top: -2 }}
                alt=""
                src="/img/quoteLeft.png"
              />
              <p
                style={{
                  position: "relative",
                  zIndex: 2,
                  fontFamily: "Georgia, Times New Roman, serif",
                  fontSize: "13px",
                  fontStyle: "italic",
                  lineHeight: 1.6,
                  color: "#000",
                  margin: "0 80px",
                }}
              >
                We at Shaagird Foundation express gratitude for the thoughtful
                and compassionate gesture on your part. This valuable
                contribution will greatly help us in restoring smiles.
              </p>
              <img
                style={{
                  position: "absolute",
                  width: 30,
                  right: -10,
                  bottom: 0,
                }}
                alt=""
                src="/img/quoteRight.png"
              />
            </div>

            {/* Receipt details: labels and values left-aligned; dotted line extends right */}
            <div
              style={{
                fontFamily: "Arial, Helvetica, sans-serif",
                fontSize: "14px",
                color: "#000",
                marginTop: "28px",
                lineHeight: 1.5,
                textAlign: "left",
              }}
            >
              {[
                {
                  label: "Receipt No:",
                  value: paymentData?.receipt_no || "N/A",
                },
                {
                  label: "Received with Thanks from:",
                  value: paymentData?.mentor_name || "N/A",
                },
                { label: "Address:", value: paymentData?.address || "N/A" },
                {
                  label: "The Sum of Rupees:",
                  value: `₹${paymentData?.amount ?? "0"}`,
                },
                { label: "For/As Donations/Grant:", value: "Donation" },
                {
                  label: "Through:",
                  value: paymentData?.through || "Paid Online",
                },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    marginBottom: "10px",
                    minHeight: "22px",
                  }}
                >
                  <span
                    style={{
                      flex: "0 0 61px",
                      // minWidth: "220px",
                      whiteSpace: "nowrap",
                      fontWeight: 400,
                    }}
                  >
                    {label}
                  </span>
                  <span
                    style={{
                      flex: 1,
                      borderBottom: "2px dotted #000",
                      marginLeft: "8px",
                      fontWeight: "bold",
                      textAlign: "left",
                      minWidth: 0,
                    }}
                  >
                    {value}
                  </span>
                </div>
              ))}
              {/* Transaction + Date on one row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  marginBottom: "10px",
                  minHeight: "22px",
                }}
              >
                <span
                  style={{
                    flex: "0 0 80px",
                    // minWidth: "220px",
                    whiteSpace: "nowrap",
                    fontWeight: 400,
                  }}
                >
                  Transaction:
                </span>
                <span
                  style={{
                    flex: 1,
                    borderBottom: "2px dotted #000",
                    marginLeft: "8px",
                    fontWeight: "bold",
                    textAlign: "left",
                    marginRight: "12px",
                  }}
                >
                  {paymentData?.transaction_id || "N/A"}
                </span>
                <span
                  style={{
                    flex: "0 0 60px",
                    minWidth: "60px",
                    whiteSpace: "nowrap",
                    fontWeight: 400,
                  }}
                >
                  Date:
                </span>
                <span
                  style={{
                    flex: 1,
                    borderBottom: "2px dotted #000",
                    marginLeft: "8px",
                    fontWeight: "bold",
                    textAlign: "left",
                    minWidth: 0,
                  }}
                >
                  {formatDate(paymentData?.payment_date)}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  marginBottom: "10px",
                  minHeight: "22px",
                }}
              >
                <span
                  style={{
                    flex: "0 0 80px",
                    // minWidth: "220px",
                    whiteSpace: "nowrap",
                    fontWeight: 400,
                  }}
                >
                  PAN Number:
                </span>
                <span
                  style={{
                    flex: 1,
                    borderBottom: "2px dotted #000",
                    marginLeft: "8px",
                    fontWeight: "bold",
                    textAlign: "left",
                    minWidth: 0,
                  }}
                >
                  {paymentData?.pan_number || "N/A"}
                </span>
              </div>
            </div>

            {/* Footer: left-aligned note, same vertical line as receipt details */}
            <div
              style={{
                display: "flex",
                marginTop: "56px",
                fontFamily: "Georgia, Times New Roman, serif",
                fontSize: "12px",
                color: "#000",
                fontWeight: "bold",
                alignItems: "flex-end",
              }}
            >
              <div style={{ flex: 1, textAlign: "left" }}>
                <p
                  style={{
                    margin: 0,
                    fontSize: "12px",
                    lineHeight: 1.5,
                    fontWeight: 400,
                    textAlign: "left",
                  }}
                >
                  <b>Note:</b> Subject to realisation of cheque
                  <br />
                  Provisional Approval U/S 80G of Income Tax Act 1961 Under
                  <br />
                  Unique Registration Number <b>ABJCS7197KE20231</b>
                </p>
              </div>
              <div style={{ position: "relative", right: 52, flexShrink: 0 }}>
                <img
                  src="/img/stamp.png"
                  alt="Stamp"
                  style={{
                    display: "block",
                    width: "100px",
                    marginBottom: "10px",
                  }}
                />
                <p
                  style={{
                    margin: 0,
                    fontFamily: "Georgia, Times New Roman, serif",
                    fontSize: "12px",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    position: "absolute",
                    bottom: 14,
                    left: -15,
                    fontWeight: 400,
                  }}
                >
                  Authorized Signature
                  <br />
                  <span>Shaagird Foundation</span>
                </p>
              </div>
            </div>
          </div>
          <img
            src="/img/receiptFooter.png"
            alt="Receipt Footer"
            style={{ position: "absolute", bottom: 0, left: 0, width: "100%" }}
          />
        </div>
      </div>

      {/* Responsive Styles */}
      <style jsx>{`
        @media (max-width: 768px) {
          .receipt {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            border-radius: 8px !important;
          }

          .d-flex.align-items-center.justify-content-between {
            flex-direction: column !important;
            gap: 10px !important;
            align-items: stretch !important;
          }

          .d-flex.align-items-center.justify-content-between h5 {
            text-align: center !important;
            margin-bottom: 0 !important;
          }

          .d-flex.align-items-center.justify-content-between button {
            width: 100% !important;
            max-width: none !important;
          }
        }

        @media (max-width: 480px) {
          .receipt {
            border-radius: 6px !important;
          }
        }
      `}</style>
    </>
  );
};

export default CertificateHtml;
